import API from '@apis/client';
import { SaleProductOptionPayload } from '@recoil/service/SaleProductAlarm';
import { AxiosResponse } from 'axios';
import {
  getMaterialGradePayload,
  getOneCategoryPayload,
  getOptionsPayload,
  getProductCategoryPayload,
  getTypeFirstPayload,
  getTypeSecondPayload,
  getTypeThirdPayload,
} from 'models/Supplement';
import { CommonAxiosErrorType } from 'models/SupplementType';
import {
  ProductEtcGetEtcProductMaterialGradeData,
  ProductEtcGetEtcProductMaterialGradePayload,
  ProductGetAllStandardCodeData,
  ProductGetMaterialGradeData,
  ProductGetMaterialGradeError,
  ProductGetOneCategoryData,
  ProductGetOneCategoryError,
  ProductGetOptionsData,
  ProductGetOptionsError,
  ProductGetProductCategoryData,
  ProductGetProductCategoryError,
  ProductGetProductTypeData,
  ProductGetProductTypesData,
  ProductGetProductTypesError,
  ProductGetProductWeightData,
  ProductGetProductWeightPayload,
  ProductLogPostMaterialGradeSearchLogData,
  ProductLogPostMaterialGradeSearchLogError,
  ProductLogPostMaterialGradeSearchLogPayload,
  ProductSaleGetSaleProductOptionData,
  ProductSaleGetSaleProductOptionError,
  ProductSupplierGetSupplierDealProductData,
  ProductSupplierGetSupplierDealProductError,
  ProductSupplierGetSupplierDealProductOptionData,
  ProductSupplierGetSupplierDealProductOptionError,
  ProductTypeGetTypeFirstData,
  ProductTypeGetTypeFirstError,
  ProductTypeGetTypeSecondData,
  ProductTypeGetTypeSecondError,
  ProductTypeGetTypeThirdData,
  ProductTypeGetTypeThirdError,
  SellableProductRequest,
  SellableProductResponse,
} from 'models/swagger/data-contracts';

/**
 * @description 표준 코드를 조회한다.
 *
 * @tags product
 * @name StandardCode
 * @summary 표준 코드를 조회한다.
 * @request GET:/product/standard_code
 **/
export const getStandardCodeApi = (): Promise<AxiosResponse<ProductGetAllStandardCodeData>> => {
  return API.get(`/product/standard_code`);
};

/**
 * @description 카테고리를 조회한다.
 *
 * @tags product
 * @name ProductGetOneCategory
 * @summary 카테고리를 조회한다.
 * @request GET:/product/one_category
 **/
export const getProductOneCategoryApi = ({
  category,
}: getOneCategoryPayload): Promise<AxiosResponse<ProductGetOneCategoryData, ProductGetOneCategoryError>> => {
  return API.get(`/product/one_category?category=${category}`);
};

/**
 *  product_type의 카테고리 가져오기 API
 *  @function getProductCategoryApi
 *  @param {number} product_type Product Type
 */
export const getProductCategoryApi = ({
  product_type_id,
  standard_code,
  is_etc,
}: getProductCategoryPayload): Promise<
  AxiosResponse<ProductGetProductCategoryData, ProductGetProductCategoryError>
> => {
  const str_is_etc = is_etc === null ? '' : is_etc;
  return API.get(
    `/product/category?product_type=${product_type_id}&standard_code=${standard_code}&is_etc=${str_is_etc}`,
  );
};

/**
 *  카테고리로 Material Grade 가져오기 API
 *  @function getMaterialGradeApi
 *  @param {number} page 페이지
 *  @param {number} category_id Category ID
 *  @param {string} material_grade Material Grade
 */
export const getMaterialGradeApi = ({
  page,
  category_id,
  material_grade,
  standard_code,
}: getMaterialGradePayload): Promise<AxiosResponse<ProductGetMaterialGradeData, ProductGetMaterialGradeError>> => {
  return API.get(
    `/product/material_grade?page_size=48&page=${page}&category=${category_id}&material_grade=${material_grade}&standard_code=${standard_code}`,
  );
};

/**
 *  타입1 가져오기 API
 *  @function getTypeApi
 *  @param {string} category Category
 *  @param {string} material_grade Material Grade
 */
export const getTypeFirstApi = ({
  category,
  material_grade,
}: getTypeFirstPayload): Promise<AxiosResponse<ProductTypeGetTypeFirstData, ProductTypeGetTypeFirstError>> => {
  return API.get(`/product/type/first?category=${category}&material_grade=${material_grade}`);
};

/**
 *  타입2 가져오기 API
 *  @function getTypeSecondApi
 *  @param {string} category Category
 *  @param {string} material_grade Material Grade
 *  @param {string} type1 Type1
 */
export const getTypeSecondApi = ({
  category,
  material_grade,
  type1,
}: getTypeSecondPayload): Promise<AxiosResponse<ProductTypeGetTypeSecondData, ProductTypeGetTypeSecondError>> => {
  return API.get(`/product/type/second?category=${category}&material_grade=${material_grade}&type1=${type1}`);
};

/**
 *  타입3 가져오기 API
 *  @function getTypeThirdApi
 *  @param {string} category Category
 *  @param {string} material_grade Material Grade
 *  @param {string} type1 Type1
 *  @param {string} type2 Type2
 */
export const getTypeThirdApi = ({
  category,
  material_grade,
  type1,
  type2,
}: getTypeThirdPayload): Promise<AxiosResponse<ProductTypeGetTypeThirdData, ProductTypeGetTypeThirdError>> => {
  return API.get(
    `/product/type/third?category=${category}&material_grade=${material_grade}&type1=${type1}&type2=${type2}`,
  );
};

/**
 *  옵션 가져오기 API
 *  @function
 *  @param {string} category Category
 *  @param {string} material_grade Material Grade
 *  @param {string} type1 Type1
 *  @param {string} type2 Type2
 *  @param {string} type3 Type3
 */
export const getOptionsApi = ({
  category,
  material_grade,
  type1,
  type2,
  type3,
}: getOptionsPayload): Promise<AxiosResponse<ProductGetOptionsData, ProductGetOptionsError>> => {
  return API.get(
    `/product/options?category=${category}&material_grade=${material_grade}&type1=${type1}&type2=${type2}&type3=${type3}`,
  );
};

/**
 *  Material Grade 검색 로그 수집 API
 *  @function trackingMaterialGradeLogApi
 */
export const trackingMaterialGradeLogApi = ({
  category,
  click_index,
  click_result,
  click_type,
  product_type,
  search_query,
}: ProductLogPostMaterialGradeSearchLogPayload): Promise<
  AxiosResponse<ProductLogPostMaterialGradeSearchLogData, ProductLogPostMaterialGradeSearchLogError>
> => {
  return API.post(`/product/log/material_grade`, {
    category,
    click_index,
    click_result,
    click_type,
    product_type,
    search_query,
  });
};

/**
 * 조건별 판매 가능 자재 검색
 * @function getProductSellableApi
 */
export const getProductSellableApi = ({
  search_conditions,
  agg_conditions,
}: SellableProductRequest): Promise<AxiosResponse<SellableProductResponse, CommonAxiosErrorType>> => {
  return API.post('/product/sellable', { search_conditions, agg_conditions });
};

/**
 *
 * 신규 판매 가능 자재 검색
 * getSaleProductOption
 */
export const getSaleProductOption = ({
  standard_code,
  product_type,
  type1,
  category,
}: SaleProductOptionPayload): Promise<
  AxiosResponse<ProductSaleGetSaleProductOptionData, ProductSaleGetSaleProductOptionError>
> => {
  return API.get('/product/sale/product_option', { params: { product_type, standard_code, type1, category } });
};

/**
 * 제품 타입들을 조회한다.
 * @function getProductTypeApi
 */
export const getProductTypeApi = (): Promise<AxiosResponse<ProductGetProductTypeData, CommonAxiosErrorType>> => {
  return API.get('/product/product_type');
};

/**
 * 제품 무게를 조회한다.
 * @function getProductWeightApi
 */
export const getProductWeightApi = ({
  category,
  material_grade,
  size1,
  schedule,
  length,
}: ProductGetProductWeightPayload): Promise<AxiosResponse<ProductGetProductWeightData, CommonAxiosErrorType>> => {
  return API.post('/product/weight', { category, material_grade, size1, schedule, length });
};

/**
 * 기타 제품 카테고리의 Material Grade를 조회한다.
 * @function getEtcProductMaterialGradeApi
 */
export const getEtcProductMaterialGradeApi = (payload: ProductEtcGetEtcProductMaterialGradePayload): Promise<AxiosResponse<ProductEtcGetEtcProductMaterialGradeData, CommonAxiosErrorType>> => {
  return API.post('/product/etc/material_grade', payload);
};



/**
 * 제품 타입들을 조회한다.
 * @function getSupplierDealProductApi
 */
export const getSupplierDealProductApi = (
  code_id: number | undefined,
): Promise<AxiosResponse<ProductSupplierGetSupplierDealProductData, ProductSupplierGetSupplierDealProductError>> => {
  return API.get(`/product/supplier/deal?code_id=${code_id}`);
};

/**
 * 판매 옵션들을 조회한다.
 * @function getSupplierDealProductOptionApi
 */
export const getSupplierDealProductOptionApi = (
): Promise<AxiosResponse<ProductSupplierGetSupplierDealProductOptionData, ProductSupplierGetSupplierDealProductOptionError>> => {
  return API.get(`/product/supplier/deal_option`);
};
